import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon } from 'tdesign-icons-react';
import { Button, MessagePlugin, Tabs, Textarea, Tooltip } from 'tdesign-react';
import TabPanel from 'tdesign-react/es/tabs/TabPanel';
import type { TextareaProps } from 'tdesign-react/lib/textarea';

import { Star2 } from '@/components/icon';
import { labelDom, LabelDomProps } from '@/components/Label';
import { PageIds } from '@/const/tip';
import { AiType, PolishPromptReq, PolishPromptResp } from '@/interface/aigc';
import { polishPrompt } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { useTypedSelector } from '@/store';

import PolishPromptDialog from './dialog';
import styles from './index.module.less';

interface IProps extends TextareaProps {
  onChange: (key: string) => void;
  value: string;
  polish: boolean;
  fieldname: string;
  labelSetting: {
    tipIcon: boolean;
    tipText: string;
  };
  type: AiType;
}

const TextareaPrompt: React.FC<IProps> = ({ onChange, polish, labelSetting, type, ...props }) => {
  const [prompt, setPrompt] = useState<string>(props.value);
  const [newPrompt, setNewPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const maxlength = props.maxlength || 800;

  const tipList = useTypedSelector(store => store.tip);
  const { fixedVal = '', category = '' } = useTypedSelector(store => store.tag)
    .find((e: any) => e.type === type)
    || { fixedVal: '', category: '' };
  const words = fixedVal.split('、');
  const panels: { name: string, tags: string }[] = useMemo(() => {
    try {
      return JSON.parse(category) || [];
    } catch {
      return [];
    }
  }, [category]);

  useEffect(() => {
    onChange(prompt);
  }, [prompt]);

  const clickPolish = async () => {
    if (loading) return;

    if (prompt.length === 0 || prompt.length > maxlength) {
      MessagePlugin.error({
        content: prompt.length === 0 ? '请填写画面描述' : `最多输入${maxlength}字`,
      });
      return;
    }

    setLoading(true);
    closeDialog();
    const promptReq: PolishPromptReq = { text: prompt };
    const { code, data, message } = await polishPrompt(promptReq) as CgiResp<PolishPromptResp>;
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '润色失败，请点击重试',
      });
      setLoading(false);
      return;
    }

    setNewPrompt(data.polishedText);
    setLoading(false);
    openDialog();
  };

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const openDialog = () => setDialogVisible(true);
  const closeDialog = () => setDialogVisible(false);
  const handleConfirm = (confirmed: boolean) => {
    closeDialog();
    if (confirmed) setPrompt(newPrompt);
  };

  const clickWord = (word: string) => {
    const newPrompt = prompt ? `${prompt},${word}` : word;
    setPrompt(newPrompt.substring(0, maxlength));
  };

  const labelProps: LabelDomProps = {
    field: { labelSetting, props: props.fieldname, ...props },
    initFormData: '',
    requiredItem: props.required,
    pageId: PageIds.AIGC_INFO,
    tipList,
  };

  const getWordsDom = (words: string[]) => <div className={styles.wordsList}>
    {words.map((word, index) => (
      <div
        key={index}
        className={styles.word}
        onClick={() => clickWord(word)}
      >
        {word}
      </div>
    ))}
  </div>;

  return (
    <div className={styles.textWrapper}>
      {labelDom(labelProps)}
      {polish && <Button
        variant="text"
        loading={loading}
        className={styles.button}
        onClick={clickPolish}
      >
        {<div className={styles.text}>
          {loading ? '润色中' : <><Star2 />描述润色</>}
        </div>}
      </Button>}
      <Textarea
        autosize={{ minRows: 5, maxRows: 5 }}
        {...props}
        value={prompt}
        onChange={setPrompt}
        className={styles.textarea}
      />
      {fixedVal && <div className={styles.wordsContainer}>
        {getWordsDom(words)}
        <Tooltip
          placement='right-top'
          trigger='click'
          content={<>
            <Tabs
              placement={'top'}
              style={{ background: 'transparent', paddingBottom: 10 }}
              className={styles.customTabs}
              defaultValue={0}
            >
              {panels.map(({ name, tags }, index) => (
                <TabPanel key={index} value={index} label={name}>
                  {getWordsDom(tags.split('、'))}
                </TabPanel>
              ))}
            </Tabs>
          </>}
        >
          <Button
            theme='default'
            shape='square'
            style={{ width: 60, height: 28, flexShrink: 0 }}
          >
            <div className={styles.moreBtn}>
              <span>更多</span>
              <ChevronRightIcon size={16} />
            </div>
          </Button>
        </Tooltip>
      </div>}
      <PolishPromptDialog
        newPrompt={newPrompt}
        visible={dialogVisible}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        handleGenerate={clickPolish}
      />
    </div >
  );
};

export default TextareaPrompt;
